import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Light from './headers/light';
import LightAdmin from './headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Swal from 'sweetalert2';
import tw from "twin.macro";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import ScrollToTop from './ScrollToTop';
import { useTranslation } from 'react-i18next';
import Footerr from './footers/Footer';
import './ProductDetails.css';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const ProductDetails = () => {
  const { t } = useTranslation();
  const { idAndSlug } = useParams();
  const id = (idAndSlug || "").toString().split("-")[0];
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const [role, setRole] = useState('');
  const { i18n } = useTranslation();
  const idSupplier = localStorage.getItem("id");

  // --- LOGIQUE CLIENT & FIDÉLITÉ ---
  const userStr = localStorage.getItem("user");
  const userData = userStr ? JSON.parse(userStr) : null;
  const customId = userData ? userData.custom_id : null;

  const [promoCode, setPromoCode] = useState("");
  const [appliedDiscountType, setAppliedDiscountType] = useState(null); // 'loyalty' | 'promo'
  const [discountAmount, setDiscountAmount] = useState(0); 
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [appliedCode, setAppliedCode] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);
  const [expandShortDesc, setExpandShortDesc] = useState(false);
  const [expandDesc, setExpandDesc] = useState(false);

  const imgRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [origin, setOrigin] = useState('center center');

  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customer, setCustomer] = useState({ Name: "", Email: "", Phone: "", Address: "", City: "", notes: '' });
  const checkoutRef = useRef(null);

  // Chargement des points de fidélité
  useEffect(() => {
    if (customId) {
      axios.get(`https://jolyaparis.cloud/api/loyalty/${customId}`)
        .then(res => {
          const pointsActifs = res.data.points || res.data.points_actifs || 0;
          setLoyaltyPoints(pointsActifs);
          
          if (role === "client_fidele" && !promoCode) {
            setPromoCode(customId);
          }
        })
        .catch(err => console.error("Erreur chargement points:", err));
    }
  }, [customId, role]);

  useEffect(() => {
    const userRole = localStorage.getItem('role');
    setRole(userRole);

    const fetchProductDetails = async () => {
      if (!id) return;
      try {
        const response = await fetch(`https://jolyaparis.cloud/api/products/${id}?lang=${i18n.language}`);
        if (!response.ok) throw new Error("Product not found");
        const data = await response.json();
        setProduct(data);
        setSelectedImage(data.image);
      } catch (error) {
        console.error(error);
      }
    };
    fetchProductDetails();
  }, [id, i18n.language]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!id) return;
      try {
        const response = await fetch(`https://jolyaparis.cloud/api/same-category/${id}?lang=${i18n.language}`);
        if (!response.ok) throw new Error("Related products not found");
        const data = await response.json();
        setRelatedProducts(data.products);
      } catch (error) {
        console.error(error);
      }
    };
    fetchRelatedProducts();
  }, [id, i18n.language]);

  // CALCUL DU SOUS-TOTAL ET REMISES (10% Automatique pour client_fidele + option points)
  const currentPrice = (role === 'supplier' || role === 'influenceur') ? product?.distributeur_price : product?.user_price;
  const subtotal = (currentPrice || 0) * quantity;

  useEffect(() => {
    let totalDiscount = 0;

    // 1. Remise automatique de 10% pour les clients fidèles
    if (role === "client_fidele") {
      totalDiscount += subtotal * 0.10;
    }

    // 2. Remise additionnelle par points si explicitement appliqués
    if (appliedDiscountType === "loyalty") {
      const pointsDH = loyaltyPoints * 10;
      totalDiscount += pointsDH;
    } else if (appliedDiscountType === "promo") {
      totalDiscount = subtotal * 0.10;
    }

    setDiscountAmount(parseFloat(totalDiscount.toFixed(2)));
  }, [quantity, subtotal, appliedDiscountType, loyaltyPoints, role]);

  const finalTotal = Math.max(0, Number((subtotal - discountAmount).toFixed(2)));

  // APPLICATION DU CODE PROMO OU FIDÉLITÉ
  const handleApplyPromo = async () => {
    const trimmedCode = promoCode.trim().toUpperCase();
    if (!trimmedCode) return;

    setIsApplyingPromo(true);

    try {
      const isFideliteCode = trimmedCode.startsWith("CF") || trimmedCode === (customId ? customId.toUpperCase() : "");

      if (isFideliteCode) {
        const loyaltyRes = await axios.get(`https://jolyaparis.cloud/api/loyalty/${trimmedCode}`);
        const pointsActifs = loyaltyRes.data.points || loyaltyRes.data.points_actifs || 0;
        const valeurDH = loyaltyRes.data.valeur_dh || (pointsActifs * 10);

        setLoyaltyPoints(pointsActifs);

        if (pointsActifs <= 0) {
          Swal.fire("Information", "Ce code de fidélité n'a aucun point actif (0 DH).", "info");
          setDiscountAmount(role === "client_fidele" ? subtotal * 0.10 : 0);
          setAppliedDiscountType(null);
          setAppliedCode("");
          return;
        }

        setAppliedDiscountType("loyalty");
        setAppliedCode(trimmedCode);

        Swal.fire({
          icon: "success",
          title: "Code fidélité appliqué !",
          text: `Remise supplémentaire de -${valeurDH}.00 DH (${pointsActifs} PTS)`
        });
        return;
      }

      const response = await axios.get(`https://jolyaparis.cloud/api/supplier-from-code/${trimmedCode}`);

      if (response.data) {
        setAppliedDiscountType("promo");
        setAppliedCode(trimmedCode);

        Swal.fire({
          icon: "success",
          title: "Code promo appliqué !",
          text: "Remise de -10% sur votre commande"
        });
      } else {
        Swal.fire({ icon: "error", title: "Code promo invalide" });
        setDiscountAmount(role === "client_fidele" ? subtotal * 0.10 : 0);
        setAppliedDiscountType(null);
        setAppliedCode("");
      }

    } catch (error) {
      console.error("Erreur promo/loyalty:", error);
      Swal.fire({ icon: "error", title: "Code invalide ou introuvable" });
      setDiscountAmount(role === "client_fidele" ? subtotal * 0.10 : 0);
      setAppliedDiscountType(null);
      setAppliedCode("");
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleAddToCart = () => {
    if (!product || product.StockQuantity <= 0) {
      Swal.fire({
        title: t('Stock_epuise'),
        text: `${product?.Name} ${t('Produit_rupture')}`,
        icon: 'error',
        confirmButtonText: t('ok')
      });
      return false;
    }

    if (quantity > product.StockQuantity) {
      Swal.fire({
        title: t('Quantite_insuffisante'),
        text: `${t('Produit_rupture')}.`,
        icon: 'warning',
        confirmButtonText: t('ok')
      });
      return false;
    }

    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const isSpecialRole = ['supplier', 'influenceur'].includes(role);
    const existingProduct = cart.find(item => item.productID === product.ProductID);

    if (existingProduct) {
      existingProduct.quantity += quantity;
      if (existingProduct.quantity > product.StockQuantity) {
        existingProduct.quantity = product.StockQuantity;
      }
    } else {
      cart.push({
        productID: product.ProductID,
        productName: product.Name,
        productImage: product.image,
        priceGain: product.user_price,
        productPrice: isSpecialRole ? product.distributeur_price : product.user_price,
        quantity: quantity
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));

    Swal.fire({
      icon: 'success',
      title: i18n.language === 'ar' ? 'تم تحديث السلة!' : 'Panier mis à jour!',
      confirmButtonColor: '#4e7662',
      confirmButtonText: 'OK',
      returnFocus: false 
    }).then(() => {
      setTimeout(() => {
        checkoutRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    });

    return true;
  };

  const handleBuyNow = () => {
    const success = handleAddToCart();
    if (success) {
      setShowCheckoutForm(true);
      setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  };

  const handleWhatsAppOrder = () => {
    const phoneNumber = "212661653922";
    const message = `Bonjour, je souhaite commander le produit : *${product.Name}*`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!/^0[5-7][0-9]{8}$/.test(customer.Phone)) {
      Swal.fire("Erreur", "Numéro de téléphone invalide.", "error");
      setIsSubmitting(false);
      return;
    }

    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    if (cart.length === 0) {
      Swal.fire("Erreur", "Votre panier est vide.", "error");
      setIsSubmitting(false);
      return;
    }

    let supplierId = idSupplier;
    
    // Les points sont déduits UNIQUEMENT si l'utilisateur a cliqué sur Appliquer le code fidélité
    const isPointsApplied = appliedDiscountType === 'loyalty';
    let paymentType = isPointsApplied ? 'point' : 'COD';
console.log("paymentType ",paymentType);
    let finalPromoCode = (appliedDiscountType === 'loyalty' || appliedDiscountType === 'promo') ? promoCode.trim() : '';

    if (appliedCode && appliedDiscountType === 'promo') {
      try {
        const response = await axios.get(`https://jolyaparis.cloud/api/supplier-from-code/${appliedCode}`);
        supplierId = response.data?.supplier_id || response.data?.id || idSupplier;
      } catch (error) {
        console.error("Erreur récupération supplier ID");
      }
    }

    try {
      const custRes = await axios.post("https://jolyaparis.cloud/api/customers/create", { 
        ...customer, 
        SupplierID: supplierId 
      });

      const customerId = custRes.data?.customer?.CustomerID || custRes.data?.id;
      if (!customerId) {
        throw new Error("Impossible de créer ou récupérer le client.");
      }

      const orderData = {
        product_ids: cart.map(i => i.productID),
        prices: cart.map(i => i.productPrice),
        quantities: cart.map(i => i.quantity),
        customer_id: customerId,
        supplierId: supplierId || idSupplier,
        notes: customer.notes || "",
        total: finalTotal,
        total_price: finalTotal,
        payment_type: paymentType,
        promo_code: finalPromoCode,
        use_points: isPointsApplied,
        discount_amount: discountAmount
      };
console.log(orderData);
      await axios.post("https://jolyaparis.cloud/api/orders/create", orderData);

      Swal.fire(t("Commande_reussie"), "", "success");
      localStorage.removeItem("cart");
      navigate("/");
    } catch (error) { 
      console.error("Order error:", error.response?.data || error.message);
      Swal.fire("Erreur!", error.response?.data?.message || "Problème lors de la commande", "error"); 
    } finally { 
      setIsSubmitting(false); 
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    const pathStr = typeof imagePath === 'object' ? (imagePath.image_path || '') : imagePath;
    if (!pathStr) return '';
    return pathStr.startsWith('http') ? pathStr : `https://jolyaparis.cloud/storage/${pathStr.replace(/^\/+/, '')}`;
  };

  if (!product) return <div className="loading">{t('loading')}...</div>;

  const extraImages = (product.images || []).map(img => {
    if (typeof img === 'string') return img;
    return img.image_path || img.url || null;
  }).filter(Boolean);

  const allImages = Array.from(new Set([product.image, ...extraImages])).filter(Boolean);

  const CardButton = tw(PrimaryButtonBase)`text-sm flex justify-center items-center mt-4`;
  const CardButtonContainer = tw.div`flex justify-center items-center mt-2 w-auto`;

  return (
    <AnimationRevealPage>
      <ScrollToTop />
      {role === 'supplier' ? <Light /> : role === 'admin' ? <LightAdmin /> : <Light />}

      {/* Top banner with breadcrumb */}
      <div
        style={{
          backgroundColor: '#f5f5f5',
          height: 150,
          display: 'flex',
          alignItems: 'center',
          width: '100vw',
          marginLeft: 'calc(50% - 50vw)',
          marginRight: 'calc(50% - 50vw)'
        }}
        className="mb-4"
      >
        <div className="container text-center">
          <h2 className="mb-1" style={{ color: '#1f2937', fontWeight: 700, fontSize: 24 }}>Shop</h2>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 justify-content-center">
              <li className="breadcrumb-item"><Link to="/">Home</Link></li>
              <li className="breadcrumb-item"><Link to="/Boutique">Shop</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Product Details</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="container mt-5">
        <div className="row">
          {/* Left Column: Main Image + Gallery Thumbnails */}
          <div className="col-md-6 mb-4">
            <div
              className="position-relative overflow-hidden"
              style={{
                width: '100%',
                height: 400,
                borderRadius: 16,
                boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                backgroundColor: '#fff'
              }}
              onMouseEnter={() => setIsZoomed(true)}
              onMouseLeave={() => setIsZoomed(false)}
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                setOrigin(`${x}% ${y}%`);
              }}
            >
              <img
                ref={imgRef}
                src={getImageUrl(selectedImage || product.image)}
                alt={product.Name}
                className="w-100 h-100"
                style={{
                  objectFit: 'contain',
                  borderRadius: 16,
                  transition: 'transform 250ms ease, transform-origin 50ms ease',
                  transform: isZoomed ? 'scale(1.6)' : 'scale(1)',
                  transformOrigin: origin,
                  cursor: 'zoom-in'
                }}
              />
            </div>

            {allImages.length > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-3 mt-3 flex-wrap">
                {allImages.map((imgSrc, index) => {
                  const currentSelected = selectedImage || product.image;
                  const active = currentSelected === imgSrc;
                  return (
                    <div
                      key={index}
                      onClick={() => setSelectedImage(imgSrc)}
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: 12,
                        padding: 3,
                        border: active ? '2px solid #2f855a' : '2px solid transparent',
                        boxShadow: active ? '0 4px 10px rgba(47, 133, 90, 0.25)' : '0 2px 6px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: '#fff'
                      }}
                    >
                      <img
                        src={getImageUrl(imgSrc)}
                        alt={`Thumbnail ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right Column: Details & Actions */}
          <div className="col-md-6">
            <p className="text-muted mb-1" style={{ fontSize: '14px' }}>{product.Category}</p>

            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <h2 className="mb-0" style={{ color: '#000000', fontSize: '28px' }}>{product.Name}</h2>
              {product.StockQuantity > 0 && (
                <span className="badge rounded-pill" style={{ backgroundColor: '#e7f5ec', color: '#2f855a', border: '1px solid #2f855a' }}>
                  {i18n.language === 'ar' ? 'متوفر' : 'En stock'}
                </span>
              )}
            </div>

            <div className="d-flex align-items-center mb-2">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="bi bi-star-fill me-1" style={{ color: '#f59e0b' }}></i>
              ))}
              {product.reviews_count ? (
                <span className="text-muted ms-2" style={{ fontSize: '14px' }}>
                  {product.reviews_count} {i18n.language === 'ar' ? 'مراجعات' : 'Avis'}
                </span>
              ) : null}
            </div>

            {(() => {
              const currentPrice = (role === 'supplier' || role === 'influenceur') ? product.distributeur_price : product.user_price;
              const compareAt = product.old_price || product.compare_at_price;
              return (
                <div className="mb-2">
                  <span style={{ color: '#e0a100', fontSize: '22px', fontWeight: 700 }}>
                    {currentPrice} {t('currency')}
                  </span>
                  {compareAt && (
                    <span className="text-muted text-decoration-line-through ms-3" style={{ fontSize: '18px' }}>
                      {compareAt} {t('currency')}
                    </span>
                  )}
                </div>
              );
            })()}

            <div
              className="text-muted"
              style={{
                lineHeight: '1.8rem',
                fontSize: '16px',
                ...(expandShortDesc
                  ? {}
                  : {
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }),
              }}
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
            <button
              type="button"
              className="btn btn-link p-0 mt-2"
              onClick={() => setExpandShortDesc((v) => !v)}
              style={{ color: '#1e3d56' }}
            >
              {i18n.language === 'ar'
                ? expandShortDesc
                  ? 'عرض أقل'
                  : 'عرض المزيد'
                : expandShortDesc
                ? 'Voir moins'
                : 'Voir plus'}
            </button>

            {product.StockQuantity === 0 && (
              <p className="mb-4" style={{ color: 'red' }}>{t('out_of_stock')}</p>
            )}

            {/* --- BOUTONS D'ACTION --- */}
            <div className="mt-4">
              <div className="row g-2 align-items-center mb-3">
                <div className="col-4">
                  <label htmlFor="quantity" className="form-label mb-1 small fw-bold text-muted">{t('quantity', 'Quantité')}</label>
                  <input
                    type="number"
                    className="form-control text-center shadow-sm"
                    id="quantity"
                    value={quantity}
                    min="1"
                    max={product.StockQuantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    style={{ height: '55px', borderRadius: '10px', fontSize: '16px' }}
                  />
                </div>
                <div className="col-8">
                  <label className="form-label mb-1 small d-block">&nbsp;</label>
                  <button
                    className="btn w-100 fw-bold text-white shadow-sm"
                    onClick={handleBuyNow}
                    style={{ backgroundColor: '#4e7662', height: '55px', borderRadius: '10px', fontSize: '16px' }}
                  >
                    ⚡ {t('buy_now', 'Acheter maintenant')}
                  </button>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <button
                    className="btn w-100 fw-bold shadow-sm"
                    onClick={handleAddToCart}
                    style={{ backgroundColor: 'white', color: '#4e7662', border: '2px solid #4e7662', height: '55px', borderRadius: '10px', fontSize: '14px' }}
                  >
                    <i className="bi bi-cart-plus me-1"></i> {t('add_to_cart', 'Ajouter au panier')}
                  </button>
                </div>
                <div className="col-6">
                  <button
                    className="btn w-100 fw-bold shadow-sm"
                             onClick={() => navigate('/checkout')}
                    style={{ backgroundColor: 'white', color: '#4e7662', border: '2px solid #4e7662', height: '55px', borderRadius: '10px', fontSize: '14px' }}
                  >
                    <i className="bi bi-credit-card me-1"></i> {t('Passer_a_la_caisse', 'Passer à la caisse')}
                  </button>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn w-100 fw-bold text-white shadow-sm"
                    onClick={handleWhatsAppOrder}
                    style={{ backgroundColor: '#25D366', height: '55px', borderRadius: '10px', fontSize: '16px' }}
                  >
                    <i className="bi bi-whatsapp me-2"></i> {i18n.language === 'ar' ? 'اطلب عبر واتساب' : 'Commander via WhatsApp'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CHECKOUT FORM INTÉGRÉ AVEC CODE PROMO & FIDÉLITÉ */}
        <AnimatePresence>
          {showCheckoutForm && (
            <motion.div ref={checkoutRef} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-5 p-4 border-0 rounded-4 shadow-lg bg-white container">
              <h3 className="text-center mb-4 fw-bold" style={{ color: "#4e7662" }}>{t('Finaliser')}</h3>
              
              <form onSubmit={handleSubmitOrder} className="px-3 px-md-5">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="fw-bold mb-1 small">{t('Nom_Complet')}</label>
                    <input type="text" className="form-control p-3" required onChange={(e) => setCustomer({...customer, Name: e.target.value})} style={{ borderRadius: '10px' }} />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="fw-bold mb-1 small">{t('Numéro_de_Téléphone')}</label>
                    <input type="text" className="form-control p-3" required onChange={(e) => setCustomer({...customer, Phone: e.target.value})} style={{ borderRadius: '10px' }} />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="fw-bold mb-1 small">{t('Adresse')}</label>
                  <textarea className="form-control p-3" rows="2" required onChange={(e) => setCustomer({...customer, Address: e.target.value})} style={{ borderRadius: '10px' }}></textarea>
                </div>

                <div className="mb-4">
                  <label className="fw-bold mb-1 small">{t('Ville')}</label>
                  <textarea className="form-control p-3" rows="2" required onChange={(e) => setCustomer({...customer, City: e.target.value})} style={{ borderRadius: '10px' }}></textarea>
                </div>

                <div className="mb-3">
                  <label className="fw-bold mb-1 small">{t('note')}</label>
                  <textarea className="form-control p-3" rows="2" placeholder={t('note_texte')} onChange={(e) => setCustomer({...customer, notes: e.target.value})} style={{ borderRadius: '10px' }}></textarea>
                </div>

                {/* SECTION CODE PROMO / FIDÉLITÉ */}
                <div className="mb-4 p-3 rounded-3 border" style={{ backgroundColor: "#f8f9fa" }}>
                  {role === "client_fidele" && (
                    <div className="alert alert-success p-2 small mb-3">
                      <i className="bi bi-gift me-2"></i>
                      <strong>Statut Client Fidèle :</strong> Remise de 10% appliquée automatiquement sur votre commande !
                      <br />
                      <strong>Code Fidélité (pour utiliser vos points en sus) :</strong> {customId} ({loyaltyPoints} PTS / {loyaltyPoints * 10} DH)
                    </div>
                  )}

                  <label className="fw-bold mb-2 small">{t('Code_promo', 'Code promo/fidélité')}</label>
                  <div className="d-flex gap-2 flex-wrap flex-md-nowrap">
                    <input 
                      type="text" 
                      className="form-control p-2" 
                      placeholder="EX: CFDALAL587 ou REHAB20"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      style={{ borderRadius: '8px' }}
                    />
                    <button 
                      type="button" 
                      className="btn text-white px-4 text-nowrap" 
                      style={{ backgroundColor: "#4e7662", borderRadius: "8px" }}
                      onClick={handleApplyPromo}
                      disabled={isApplyingPromo}
                    >
                      {isApplyingPromo ? "..." : t('Appliquer_code_promo', 'Appliquer le code promo/fidélité')}
                    </button>
                  </div>
                </div>

                {/* RÉSUMÉ DU SOUS-TOTAL ET TOTAL */}
                <div className="p-3 mb-4 rounded-3 border">
                  <div className="d-flex justify-content-between mb-2">
                    <span>{t('Sous_total', 'Sous-total')} :</span>
                    <span className="fw-bold">{subtotal.toFixed(2)} DH</span>
                  </div>
                  
                  {role === "client_fidele" && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Remise Client Fidèle (-10%)</span>
                      <span>-{(subtotal * 0.10).toFixed(2)} DH</span>
                    </div>
                  )}

                  {appliedDiscountType === "loyalty" && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Remise Points Fidélité ({loyaltyPoints} PTS)</span>
                      <span>-{(loyaltyPoints * 10).toFixed(2)} DH</span>
                    </div>
                  )}

                  {appliedDiscountType === "promo" && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Remise Code Promo (-10%)</span>
                      <span>-{(subtotal * 0.10).toFixed(2)} DH</span>
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <span className="fw-bold fs-5">{t('Total', 'Total')}</span>
                    <span className="fw-bold fs-4" style={{ color: "#4e7662" }}>
                      {finalTotal.toFixed(2)} DH
                    </span>
                  </div>
                </div>

                <button type="submit" disabled={isSubmitting} className="btn w-100 py-3 text-white fw-bold shadow mt-3" style={{ backgroundColor: '#4e7662', borderRadius: '10px', fontSize: '18px' }}>
                  {isSubmitting ? t('Chargement') : t('Confirmer_commande')}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Full Description */}
        <div className="p-4" style={{ marginTop: '2rem', paddingTop: '1rem', paddingBottom: '1rem' }}>
          <h1 className="mt-5 text-center" style={{ color: "#428964", fontSize: "1.5rem", marginBottom: '20px' }}>Description</h1>
          <div
            className="text-muted"
            style={{
              lineHeight: '1.8rem',
              fontSize: '16px',
              ...(expandDesc
                ? {}
                : {
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }),
            }}
            dangerouslySetInnerHTML={{ __html: product.Description }}
          />
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-link p-0 mt-2"
              onClick={() => setExpandDesc((v) => !v)}
              style={{ color: '#1e3d56' }}
            >
              {i18n.language === 'ar'
                ? expandDesc
                  ? 'عرض أقل'
                  : 'عرض المزيد'
                : expandDesc
                ? 'Voir moins'
                : 'Voir plus'}
            </button>
          </div>
        </div>

        {/* Related Products */}
        <h1 className="mt-5 text-center" style={{ color: "#000000", fontSize: "1.5rem" }}>{t('Vous_aimerez')}</h1>
        <br /><br />

        <div className="row">
          {relatedProducts.length > 0 ? (
            relatedProducts.slice(0, 4).map((relatedProduct) => (
              <div key={relatedProduct.ProductID} className="col-md-3">
                <div className="card border-0">
                  <Link to={`/product-details/${relatedProduct.ProductID}`}>
                    <img
                      src={getImageUrl(relatedProduct.image)}
                      className="card-img-top"
                      alt={relatedProduct.Name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Link>
                  <div className="card-body text-center">
                    <h5 className="card-title text-truncate" style={{ fontSize: "14px", color: "#000000", marginBottom: "0.5rem" }}>
                      {relatedProduct.Name}
                    </h5>
                    <p className="card-text" style={{ fontSize: "16px", fontWeight: "semibold", color: "#000000" }}>
                      {role === "supplier"
                        ? `${relatedProduct.distributeur_price} ${t("currency")}`
                        : `${relatedProduct.user_price} ${t("currency")}`}
                    </p>
                    <CardButtonContainer>
                      <Link to={`/product-details/${relatedProduct.ProductID}`}>
                        <CardButton>{t('Acheter')}</CardButton>
                      </Link>
                    </CardButtonContainer>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>{t('Aucun_produit_similaire_trouve')}.</p>
          )}
        </div>
      </div>

      <Footerr />
    </AnimationRevealPage>
  );
};

export default ProductDetails;